import React from 'react';
import { clsx } from 'clsx';
import { Search, ChevronRight } from 'lucide-react';

export interface HeaderProps {
  breadcrumbs?: { label: string; page?: string }[];
  onSearch?: () => void;
  isDarkMode?: boolean;
  actions?: React.ReactNode;
}

export function Header({ breadcrumbs, onSearch, isDarkMode = false, actions }: HeaderProps) {
  return (
    <header
      className={clsx(
        'h-16 flex items-center justify-between px-6 border-b sticky top-0 z-20',
        isDarkMode
          ? 'bg-gray-900 border-gray-800 text-white'
          : 'bg-white border-gray-200 text-gray-900'
      )}
    >
      {/* Breadcrumbs */}
      <nav className="flex items-center gap-2 text-sm">
        {breadcrumbs?.map((crumb, idx) => (
          <React.Fragment key={idx}>
            {idx > 0 && (
              <ChevronRight
                className={clsx(
                  'w-4 h-4',
                  isDarkMode ? 'text-gray-600' : 'text-gray-300'
                )}
              />
            )}
            <span
              className={clsx(
                idx === (breadcrumbs?.length ?? 0) - 1
                  ? isDarkMode
                    ? 'text-white font-medium'
                    : 'text-gray-900 font-medium'
                  : isDarkMode
                    ? 'text-gray-500'
                    : 'text-gray-400'
              )}
            >
              {crumb.label}
            </span>
          </React.Fragment>
        ))}
      </nav>

      <div className="flex items-center gap-3">
        {/* Search trigger */}
        {onSearch && (
          <button
            onClick={onSearch}
            className={clsx(
              'flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors',
              isDarkMode
                ? 'border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-300'
                : 'border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-500'
            )}
          >
            <Search className="w-4 h-4" />
            <span>Search...</span>
            <kbd
              className={clsx(
                'px-1.5 py-0.5 text-xs rounded font-mono',
                isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'
              )}
            >
              Ctrl+K
            </kbd>
          </button>
        )}
        {actions}
      </div>
    </header>
  );
}
